import { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from '@packages/utils';
import { ENotificationType } from '@idaho-aeyc/ui-kit';
import {
  useGetContactsQuery,
  useExportContactsMutation,
} from 'redux/apiSlice/elcs.slice';
import { EContactsColumnKey, TContactsUI } from 'data/types/elcs.types';
import useTable from 'components/Base/Table/hooks/useTable';
import {
  ETableStoragePaths,
  TColumnsState,
} from 'components/Base/Table/Table.types';
import { useGetTagsListQuery } from 'redux/apiSlice/tags.slice';
import { ETagType } from 'data/types/tags.types';
import { useCheckPermission } from 'hooks/useCheckPermission';
import { TabBodyWrapper } from 'components/Base/Tabs';
import { EIconNames } from 'components/Base/Icon';
import { Table } from 'components/Base/Table';
import CardContent from 'components/Base/Layout/Content/CardContent';
import useNavigateWithBackState from 'hooks/useNavigateWithBackState';
import ExportDropdown from 'components/Shared/ExportDropdown';
import { useShowToastNotification } from 'components/Base/Notification';
import { appLocalStorage, EStorageNames } from 'helpers/storage';
import useGetData from './ELACMembersTab.data';
import { TELACMembersTabProps } from './ELACMembersTab.types';
import AddELACMembers from './ELACMembersActions/AddELACMembers';

const COLUMN_KEY_TO_EXPORT_NAME: Record<string, string> = {
  [EContactsColumnKey.FIRST_NAME]: 'firstName',
  [EContactsColumnKey.LAST_NAME]: 'lastName',
  title: 'title',
  email: 'email',
  phone: 'phone',
  [EContactsColumnKey.TAGS]: 'tags',
  isElacMember: 'isElacMember',
};

const SORT_FIELD_MAP: Record<string, string> = {
  [EContactsColumnKey.FIRST_NAME]: 'FIRST_NAME',
  [EContactsColumnKey.LAST_NAME]: 'LAST_NAME',
};

const ElacMembers = ({ elcId }: TELACMembersTabProps) => {
  const { t } = useTranslation({
    keyPrefix: 'descriptions.admin-portal.elcs.detail.elac-members-tab.list',
  });

  const isContactEditable = useCheckPermission('ELC_CONTACTS_EDIT');
  const { defaultColumns } = useGetData(elcId, isContactEditable);
  const {
    page,
    pageSize,
    setPageSize,
    sortColumn,
    selectedFilters,
    hasSelectedFilter,
    onFiltersReset,
    columns,
    setColumns,
    handleTableChange,
    selectedRows,
    setSelectedRows,
  } = useTable<TContactsUI>({
    storagePath: ETableStoragePaths.ELAC_MEMBERS,
    defaultColumns,
  });

  const { data, isFetching, isLoading } = useGetContactsQuery({
    page,
    pageSize,
    sortColumn,
    filters: selectedFilters,
    elcId,
    isElacMember: true,
  });

  const { data: tagsFilters, isSuccess: tagsFiltersSuccess } =
    useGetTagsListQuery(ETagType.PEOPLE);

  useEffect(() => {
    if (tagsFiltersSuccess) {
      setColumns(p => {
        return p.map(item => {
          if (item.key === EContactsColumnKey.TAGS && tagsFiltersSuccess) {
            item.filters = tagsFilters;
            item.filterSearch = true;
          }
          return item;
        });
      });
    }
  }, [tagsFiltersSuccess, tagsFilters]);

  const { goPeopleDetails } = useNavigateWithBackState();

  const navigateToPeopleDetails = (record: TContactsUI) => {
    return {
      onClick: () => goPeopleDetails(record.userId, t('TITLE')),
    };
  };

  const [exportContacts, { isLoading: isExporting }] =
    useExportContactsMutation();
  const { showToastNotification } = useShowToastNotification();

  const getVisibleExportColumns = useCallback(() => {
    const tableSettings = appLocalStorage.getItem(EStorageNames.tableSettings);
    const columnsState = tableSettings?.[
      ETableStoragePaths.ELAC_MEMBERS
    ] as TColumnsState[];
    if (!columnsState) {
      return Object.values(COLUMN_KEY_TO_EXPORT_NAME);
    }
    return columnsState
      .filter(col => col.show)
      .map(col => COLUMN_KEY_TO_EXPORT_NAME[col.key as string])
      .filter(Boolean);
  }, []);

  const buildExportFilters = useCallback(
    (ids?: string[]) => {
      const filters: Record<string, unknown> = {
        isElacMember: true,
      };
      if (ids?.length) {
        filters.ids = ids;
      }
      if (selectedFilters?.firstName?.[0]) {
        filters.firstName = selectedFilters.firstName[0] as string;
      }
      if (selectedFilters?.lastName?.[0]) {
        filters.lastName = selectedFilters.lastName[0] as string;
      }
      if (selectedFilters?.email?.[0]) {
        filters.email = selectedFilters.email[0] as string;
      }
      if (selectedFilters?.tagIds) {
        filters.tags = selectedFilters.tagIds;
      }
      return filters;
    },
    [selectedFilters],
  );

  const buildExportSort = useCallback(() => {
    if (!sortColumn.sortField || !sortColumn.sortDirection) return undefined;
    const field = SORT_FIELD_MAP[sortColumn.sortField];
    if (!field) return undefined;
    return { field, direction: sortColumn.sortDirection };
  }, [sortColumn]);

  const handleExportAll = useCallback(async () => {
    try {
      const exportColumns = getVisibleExportColumns();
      await exportContacts({
        elcId,
        columns: exportColumns,
        filters: buildExportFilters(),
        ...(buildExportSort() && { sort: buildExportSort() }),
      }).unwrap();
    } catch {
      showToastNotification({
        message: 'Export failed. Please try again.',
        type: ENotificationType.ERROR,
      });
    }
  }, [
    exportContacts,
    elcId,
    getVisibleExportColumns,
    buildExportFilters,
    buildExportSort,
    showToastNotification,
  ]);

  const handleExportSelected = useCallback(async () => {
    const { selectedRowsKeys } = selectedRows;

    if (!selectedRowsKeys?.length) {
      showToastNotification({
        message: 'Please select at least one row to export',
        type: ENotificationType.ERROR,
      });
      return;
    }

    try {
      const ids = selectedRowsKeys.map(key => String(key));
      const exportColumns = getVisibleExportColumns();
      await exportContacts({
        elcId,
        columns: exportColumns,
        filters: buildExportFilters(ids),
        ...(buildExportSort() && { sort: buildExportSort() }),
      }).unwrap();
    } catch {
      showToastNotification({
        message: 'Export failed. Please try again.',
        type: ENotificationType.ERROR,
      });
    }
  }, [
    exportContacts,
    elcId,
    selectedRows,
    getVisibleExportColumns,
    buildExportFilters,
    buildExportSort,
    showToastNotification,
  ]);

  const exportAction = useMemo(
    () => (
      <ExportDropdown
        key="export-actions"
        onExportAll={handleExportAll}
        onExportSelected={handleExportSelected}
        isExporting={isExporting}
      />
    ),
    [handleExportAll, handleExportSelected, isExporting],
  );

  return (
    <CardContent tabContent contentPadding="8px 0px">
      <TabBodyWrapper
        isLoading={isLoading}
        emptyText={t('empty-view.EMPTY_TEXT')}
        emptyIcon={EIconNames.PARENT_OUTLINED}
      >
        <Table<TContactsUI>
          onFiltersReset={onFiltersReset}
          hasFilters={hasSelectedFilter}
          columns={columns}
          {...(isContactEditable && {
            additionalActions: [exportAction, <AddELACMembers elcId={elcId} />],
          })}
          dataSource={data?.content}
          rowClassName="pointer"
          onRow={navigateToPeopleDetails}
          loading={isFetching}
          pageSize={pageSize}
          setPageSize={setPageSize}
          totalDataCount={data?.totalElements}
          onChange={handleTableChange}
          rowKey={record => record.contactId}
          currentPage={page}
          storagePath={ETableStoragePaths.ELAC_MEMBERS}
          selectedRows={selectedRows}
          setSelectedRows={setSelectedRows}
        />
      </TabBodyWrapper>
    </CardContent>
  );
};

export default ElacMembers;
